import { Component, OnDestroy, OnInit, ViewChild } from '@angular/core';
import { FormControl, FormGroup } from '@angular/forms';
import { MatDialog } from '@angular/material/dialog';
import { MatPaginator, PageEvent } from '@angular/material/paginator';
import { MatSort } from '@angular/material/sort';
import { MatTableDataSource } from '@angular/material/table';
import { Router } from '@angular/router';
import { Observable, Subscription } from 'rxjs';
import { menuOptions } from 'src/data/menu-options.data';
import { OccurrenceService } from 'src/app/services/occurrence/occurrence.service';
import {
  ConsultaOcorrenciasNovasParams,
  Ocorrencia,
  OcorrenciaNovaFilterOrigem,
  OcorrenciaNovaFilterStatus,
  OcorrenciaNovaFilterTipo
} from 'src/models/interfaces/ocorrencia/ocorrencia-interface';
import { HomeComponent } from '../home/home.component';
import { LoadingService } from 'src/app/services/loading/loading.service';
import { OccurrenceLoadingKeys } from 'src/models/keys/occurrence/ocurrence.keys';
import { DialogInfoData } from 'src/models/interfaces/dialog-info-data.interface';
import { CommonService } from 'src/app/services/common/common.service';
import { showError } from 'src/utils/showErrorMessage';
import { EmptyInfoDialogComponent } from 'src/app/shared/components/empty-list-components/empty-info-dialog/empty-info-dialog.component';

interface ActiveFilter {
  key: 'idStatusOcorrencia' | 'idTipoOcorrencia' | 'idOrigemOcorrencia' | 'diasAbertura';
  value: number;
}

@Component({
  selector: 'spe-ocorrencia',
  templateUrl: './ocorrencia.component.html',
  styleUrls: ['./ocorrencia.component.scss'],
  standalone: false
})
export class OcorrenciaComponent implements OnInit, OnDestroy {
  public readonly dataSource = new MatTableDataSource<Ocorrencia>([]);
  public readonly displayedColumns: string[] = ['numero', 'dataAbertura', 'tipo', 'status', 'opcoes'];
  public loading$?: Observable<boolean>;
  public message: string = '';
  public apiStatus: string | null = null;
  public inputValue: string = '';
  public showSearchBar: boolean = false;
  public isLoading: boolean = false;
  public hasError: boolean = false;

  public currentPage: number = 0;
  public pageSize: number = 25;
  public totalElements: number = 0;

  public listaStatusOcorrencia: OcorrenciaNovaFilterStatus[] = [];
  public listaTipoOcorrencia: OcorrenciaNovaFilterTipo[] = [];
  public listaOrigemOcorrencia: OcorrenciaNovaFilterOrigem[] = [];
  public readonly listaDiasAbertura: number[] = [30, 60, 90];

  public readonly filtersForm = new FormGroup({
    idStatusOcorrencia: new FormControl<number | null>(null),
    idTipoOcorrencia: new FormControl<number | null>(null),
    idOrigemOcorrencia: new FormControl<number | null>(null),
    diasAbertura: new FormControl<number | null>(30)
  });
  public activeFilters: ActiveFilter[] = [];
  private subscriptions: Subscription[] = [];
  private readonly showErrorMessage = showError;

  public readonly emptyListDialogInfoData: DialogInfoData = {
    title: 'Tem novidade no autoatendimento!',
    contents: [
      {
        tag: 'p',
        content:
          'Agora você conta com duas novas opções de solicitação diretamente pelo site: atualizar o número do cartão e solicitar a 2ª via.'
      },
      {
        tag: 'p',
        content:
          'E para facilitar ainda mais, as ocorrências ficam centralizadas no cadastro de cada funcionário, reunindo em um só lugar as opções de atendimento disponíveis para aquele colaborador.'
      },
      {
        tag: 'p',
        class: 'mb-0',
        content: {
          tag: 'b',
          content: 'Veja como é fácil:'
        }
      },
      {
        tag: 'ol',
        class: 'mb-4',
        content: [
          {
            tag: 'li',
            content: [
              { tag: 'span', content: 'Acesse ' },
              { tag: 'b', content: 'Funcionários' },
              { tag: 'span', content: '.' }
            ]
          },
          {
            tag: 'li',
            content: [
              {
                tag: 'span',
                content: 'Localize o funcionário desejado e clique em '
              },
              { tag: 'b', content: 'Editar' },
              { tag: 'span', content: '.' }
            ]
          },
          {
            tag: 'li',
            content: [
              { tag: 'span', content: 'Role a página até a seção ' },
              { tag: 'b', content: 'Autoatendimento' },
              { tag: 'span', content: '.' }
            ]
          },
          {
            tag: 'li',
            content: [
              { tag: 'span', content: 'Clique em ' },
              { tag: 'b', content: 'Nova ocorrência' },
              { tag: 'span', content: '.' }
            ]
          },
          {
            tag: 'li',
            content: 'Escolha a opção desejada.'
          }
        ]
      },
      {
        tag: 'p',
        class: 'mb-4',
        content:
          'Vale lembrar que o seu colaborador continua contando com a praticidade do APP, onde também pode realizar suas solicitações de forma simples e rápida.'
      },
      {
        tag: 'a',
        class: 'dialog-link',
        href: '/home/funcionarios/listagem',
        target: '_self',
        content: 'Acessar página de funcionários'
      }
    ]
  };

  private readonly buildActiveFilters = (): ActiveFilter[] => {
    const filters = this.filtersForm.value;
    const result: ActiveFilter[] = [];

    if (filters.idStatusOcorrencia != null) {
      result.push({ key: 'idStatusOcorrencia', value: filters.idStatusOcorrencia });
    }
    if (filters.idTipoOcorrencia != null) {
      result.push({ key: 'idTipoOcorrencia', value: filters.idTipoOcorrencia });
    }
    if (filters.idOrigemOcorrencia != null) {
      result.push({ key: 'idOrigemOcorrencia', value: filters.idOrigemOcorrencia });
    }
    if (filters.diasAbertura != null) {
      result.push({ key: 'diasAbertura', value: filters.diasAbertura });
    }

    return result;
  };

  private readonly updateActiveFilters = (): void => {
    this.activeFilters = this.buildActiveFilters().filter((filter) => filter.key !== 'diasAbertura');
  };

  @ViewChild(MatPaginator) paginator!: MatPaginator;
  @ViewChild(MatSort) set matSort(sort: MatSort) {
    this.dataSource.sort = sort;
  }

  constructor(
    private readonly homeComponent: HomeComponent,
    private readonly routerService: Router,
    private readonly occurrenceService: OccurrenceService,
    private readonly loadingService: LoadingService,
    private readonly dialog: MatDialog,
    private readonly commonService: CommonService
  ) {}

  ngOnInit(): void {
    this.loading$ = this.loadingService.create(OccurrenceLoadingKeys.Default).loading$;
    this.updateHomeHeader();
    this.homeComponent.idleMenuContent = menuOptions[4].content;
    this.homeComponent.iconPath = 'assets/images/64_icon_id_cards.png';

    const filterSubscription = this.filtersForm.valueChanges.subscribe(() => {
      this.currentPage = 0;
      if (this.paginator) {
        this.paginator.pageIndex = 0;
      }
      this.getOcorrencias();
    });

    this.subscriptions.push(filterSubscription);

    this.getFiltros();
    this.getOcorrencias(30);
  }

  ngOnDestroy(): void {
    this.subscriptions.forEach((subscription) => subscription.unsubscribe());
  }

  private readonly updateHomeHeader = (): void => {
    this.homeComponent.setNewVariables(
      'Ocorrências',
      'Acompanhe as ocorrências abertas e use os filtros para encontrar rapidamente o que precisa.',
      undefined,
      this.isEmpty
        ? undefined
        : [
            {
              label: 'Adicionar ocorrência',
              action: this.openComoAbrirOcorrencia,
              color: 'primary',
              type: 'flat'
            }
          ]
    );
  };

  public readonly openComoAbrirOcorrencia = (): void => {
    this.dialog.open(EmptyInfoDialogComponent, {
      panelClass: 'full-bleed-dialog',
      width: '600px',
      data: this.emptyListDialogInfoData,
      autoFocus: false
    });
  };

  public readonly pageEvents = (event: PageEvent): void => {
    this.currentPage = event.pageIndex;
    this.pageSize = event.pageSize;
    this.getOcorrencias();
  };

  public readonly applySearchFilter = (): void => {
    this.currentPage = 0;
    if (this.paginator) {
      this.paginator.pageIndex = 0;
    }
    this.getOcorrencias();
  };

  /** Empty total (sem filtros/busca): layout spe-empty-list. */
  public get isEmpty(): boolean {
    return !this.hasError && this.totalElements === 0 && !this.hasListFilters;
  }

  /** Há busca ou filtro além do período padrão (30 dias). */
  private get hasListFilters(): boolean {
    const filters = this.filtersForm.value;
    const hasSearch = !!this.inputValue?.trim();
    const hasChipFilters =
      filters.idStatusOcorrencia != null || filters.idTipoOcorrencia != null || filters.idOrigemOcorrencia != null;
    const hasCustomPeriod = filters.diasAbertura != null && filters.diasAbertura !== 30;
    return hasSearch || hasChipFilters || hasCustomPeriod;
  }

  public readonly clearFilter = (key: ActiveFilter['key']): void => {
    this.filtersForm.get(key)?.setValue(null, { emitEvent: true });
  };

  public readonly clearAllFilters = (): void => {
    this.filtersForm.reset(
      {
        idStatusOcorrencia: null,
        idTipoOcorrencia: null,
        idOrigemOcorrencia: null,
        diasAbertura: 30
      },
      { emitEvent: true }
    );
  };

  public readonly trackByActiveFilter = (_index: number, filter: ActiveFilter): string => {
    return `${filter.key}:${filter.value}`;
  };

  public readonly getFilterLabel = (key: ActiveFilter['key'], value: number): string => {
    if (key === 'idStatusOcorrencia') {
      const found = this.listaStatusOcorrencia.find((item) => item.idStatusOcorrencia === value);
      return found?.descricaoStatusOcorrencia || 'Status';
    }
    if (key === 'idTipoOcorrencia') {
      const found = this.listaTipoOcorrencia.find((item) => item.idTipoOcorrencia === value);
      return found?.descricaoTipoOcorrencia || 'Tipo';
    }
    if (key === 'idOrigemOcorrencia') {
      const found = this.listaOrigemOcorrencia.find((item) => item.idOrigemOcorrencia === value);
      return found?.descricaoOrigemOcorrencia || 'Origem';
    }
    return `${value} dias`;
  };

  getStatusClass(status: string) {
    switch (status) {
      case 'Solucionado':
        return 'success-chip';
      case 'Aberto':
        return 'warning-chip';
      case 'Cancelado':
        return 'cancelled-chip';
      case 'Em Tratamento':
        return 'processed-chip';
      default:
        return 'cancelled-chip';
    }
  }

  public readonly toogleEnableByStatus = (_row: Ocorrencia): boolean => {
    return false;
  };

  public readonly goToDetails = (row: Ocorrencia): void => {
    console.log('Detalhar ocorrencia:', row.idOcorrencia);

    this.routerService.navigate(['/home/acompanhamento'], {
      queryParams: { idOcorrencia: row.idOcorrencia, idFuncionario: row.idFuncionario }
    });
  };

  private readonly getFiltros = (): void => {
    const subscription = this.occurrenceService.getOcorrenciasNovasFiltros().subscribe({
      next: (response) => {
        this.listaStatusOcorrencia = response.listaStatusOcorrencia || [];
        this.listaTipoOcorrencia = response.listaTipoOcorrencia || [];
        this.listaOrigemOcorrencia = response.listaOrigemOcorrencia || [];
      },
      error: (err) => {
        setTimeout(() => {
          const message = this.commonService.getErrorMessage(err);
          if (message !== '') {
            this.showErrorMessage(this.dialog, message);
          }
        }, 100);
      }
    });

    this.subscriptions.push(subscription);
  };

  private readonly getOcorrencias = (dias = 30): void => {
    this.isLoading = true;
    this.hasError = false;
    const params: ConsultaOcorrenciasNovasParams = {
      busca: this.inputValue?.trim() || undefined,
      idStatusOcorrencia: this.filtersForm.value.idStatusOcorrencia ?? undefined,
      idTipoOcorrencia: this.filtersForm.value.idTipoOcorrencia ?? undefined,
      idOrigemOcorrencia: this.filtersForm.value.idOrigemOcorrencia ?? undefined,
      diasAbertura: this.filtersForm.value.diasAbertura ?? dias ?? undefined,
      page: this.currentPage,
      size: this.pageSize
    };

    const subscription = this.occurrenceService.consultaOcorrenciasNovas(params).subscribe({
      next: (response) => {
        this.dataSource.data = response.content || [];
        this.totalElements = response.totalElements || 0;
        this.currentPage = response.number || 0;
        this.hasError = false;

        if (this.paginator) {
          this.paginator.length = this.totalElements;
          this.paginator.pageIndex = this.currentPage;
        }

        this.apiStatus = null;
        this.message = '';
        this.isLoading = false;
        this.updateActiveFilters();
        this.updateHomeHeader();
      },
      error: (err) => {
        this.dataSource.data = [];
        this.totalElements = 0;
        this.hasError = true;
        this.isLoading = false;
        this.apiStatus = null;
        this.message = '';
        this.updateActiveFilters();
        this.updateHomeHeader();

        setTimeout(() => {
          const message = this.commonService.getErrorMessage(err);
          this.showErrorMessage(this.dialog, message || 'Não foi possível carregar suas ocorrências. Tente novamente.');
        }, 100);
      }
    });

    this.subscriptions.push(subscription);
  };
}
